import React, { useContext, useEffect, useMemo } from 'react';
import { Alert, Box, FormField, Select, SpaceBetween } from '@amzn/awsui-components-react/polaris';
import CONSTANTS from 'src/utils/constants';
import { SegmentTablesContext } from 'src/components/segment-cdt/SegmentTablesContext';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import { ACTIONS as DS_ACTIONS, dataSourcesReducer } from 'src/services/calculation-builder/DataSourcesReducer';
import { initialState as dsInitialState } from 'src/services/calculation-builder/DataSourcesState';
import { getNewDataSourceRecord } from 'src/models/common/DataSourceRecord';
import DataSourcesMagicBox from 'src/components/calculation-builder/data-sources/MagicBox';
import DataSourcesSelectCOA from 'src/components/calculation-builder/data-sources/SelectCOA';
import CommonDataSourceOperations from 'src/services/common/CommonDataSourceOperations';
import { COADataKey, LedgerType, SegmentDataKeys, SegmentPullCriteria } from 'src/models/segment-cdt/SegmentCdtTypes';
import 'src/assets/styles/segment-gl-pull-data-source.scss';

/**
 * The optional COA dimensions, in picker order, each paired with the {@link SegmentDataKeys} field it maps
 * into. Deriving the destination from `COA_SEGMENT_MAPPING[..].WEB_API` — rather than hand-listing the eight
 * names at the mapping site — keeps the picker and the criteria in step: adding a dimension here cannot
 * reach the checkbox list while silently never reaching the emitted data keys. Mirrors the `forEach`-over-
 * `WEB_API` form the sibling services use (`DataSourcesService`, `MultidimensionalAllocationService`,
 * `DatasetService`).
 *
 * Company and GL Account are deliberately absent: they are the two required rows, live on their own
 * `dataKeyInput` fields rather than in `selectedCOA`, and are filtered out of the picker by SelectCOA.
 */
const OPTIONAL_COA_DIMENSIONS: { ui: string; key: keyof SegmentDataKeys }[] = [
    { ui: CONSTANTS.COA_SEGMENT_MAPPING.LOCATION.UI, key: CONSTANTS.COA_SEGMENT_MAPPING.LOCATION.WEB_API as keyof SegmentDataKeys },
    { ui: CONSTANTS.COA_SEGMENT_MAPPING.COST_CENTER.UI, key: CONSTANTS.COA_SEGMENT_MAPPING.COST_CENTER.WEB_API as keyof SegmentDataKeys },
    { ui: CONSTANTS.COA_SEGMENT_MAPPING.PRODUCT_LINE.UI, key: CONSTANTS.COA_SEGMENT_MAPPING.PRODUCT_LINE.WEB_API as keyof SegmentDataKeys },
    { ui: CONSTANTS.COA_SEGMENT_MAPPING.CHANNEL.UI, key: CONSTANTS.COA_SEGMENT_MAPPING.CHANNEL.WEB_API as keyof SegmentDataKeys },
    { ui: CONSTANTS.COA_SEGMENT_MAPPING.PROJECT.UI, key: CONSTANTS.COA_SEGMENT_MAPPING.PROJECT.WEB_API as keyof SegmentDataKeys },
    { ui: CONSTANTS.COA_SEGMENT_MAPPING.LG_CODE.UI, key: CONSTANTS.COA_SEGMENT_MAPPING.LG_CODE.WEB_API as keyof SegmentDataKeys },
];

// The picker's checkboxes are the fixed segment COA dimensions, which we already hold in
// COA_SEGMENT_MAPPING. Seeded synchronously so the picker renders immediately instead of blanking while a
// getCoaTypes network round-trip resolves on every open. This order also drives the checkbox order.
// LG Code is always present but SelectCOA disables its checkbox unless the row's datasource is Secondary
// Ledger (`isLgCodeDisabledForRow`), and DataSourcesReducer drops a previously-entered `LG Code` from
// selectedCOA when the datasource leaves Secondary — so the "Secondary unlocks LG Code" promise holds in
// the picker, not only at commit.
export const SEGMENT_COA_TYPES: string[] = [
    CONSTANTS.COA_SEGMENT_MAPPING.COMPANY.UI,
    CONSTANTS.COA_SEGMENT_MAPPING.GL_ACCOUNT.UI,
    ...OPTIONAL_COA_DIMENSIONS.map((d) => d.ui),
];

/**
 * The GL Balance Pull "data source" sub-form: a ledger type + a per-COA-dimension, multi-value data-key
 * selector (Company, GL Account, Location, Cost Center, Product Line, Channel, Project, and LG Code under
 * a Secondary ledger). It reuses the calculation-builder Step-2 leaves ({@link DataSourcesMagicBox} + its
 * bundled custom-rollup modal) unchanged, running its own contained {@link dataSourcesReducer} instance —
 * those components are prop-driven and read nothing from CalculationBuilderContext. `allowBalancePull` is
 * false: this captures the criteria, it does not pull a balance at define-time.
 *
 * It owns only the ledger + data keys; the parent form keeps name / period / currency. On every change it
 * maps the reused `DataSourceRecord` into the nested {@link SegmentPullCriteria} shape ({ ledgerType,
 * dataKeys }) and hands it up via `onChange`; `periodType` is merged in by the parent.
 */
export default function SegmentGlPullDataSource(props: {
    value?: SegmentPullCriteria;
    onChange: (patch: Pick<SegmentPullCriteria, 'ledgerType' | 'dataKeys'>) => void;
}) {
    const { value, onChange } = props;
    const { services } = useContext(SegmentTablesContext);
    const M = CONSTANTS.COA_SEGMENT_MAPPING;
    const DS = CONSTANTS.DATA_SOURCE_TYPES;

    /*
     * A contained DataSources reducer with a single fresh record whose `datasource` carries the ledger.
     * The parent's period/currency live outside this sub-form.
     *
     * SEEDED ONCE, and `value` is therefore effectively write-only: only `value.ledgerType` is read, and
     * `value.dataKeys` is never read back into the reducer. That is safe ONLY WHILE COLUMNS ARE ADD-ONLY —
     * SegmentTablesReducer has no edit-column action, START_COLUMN_EDITOR always builds a fresh draft, and
     * Back / CANCEL_COLUMN_EDITOR discards it, so nothing ever mounts this sub-form against a populated
     * pullCriteria. The mount-time onChange below pushes `dataKeys: {}` up, which would blank a populated
     * draft; today nothing can observe that.
     *
     * Per-column isolation relies on this component being unmounted between columns: the modal renders the
     * form only while `columnEditor` exists, so committing or cancelling unmounts it and the next open
     * re-seeds. If an "edit a committed column" path is ever added, seed from `value.dataKeys` here (and key
     * this component per column) or the previous column's keys will leak into the next one.
     */
    const [state, dispatch] = useReducerWithLogger(
        dataSourcesReducer,
        useMemo(() => {
            const record: any = getNewDataSourceRecord(0);
            record.datasource = value?.ledgerType === LedgerType.SECONDARY ? DS.SECONDARY_LEDGER : DS.GENERAL_LEDGER;
            // dataKeyInput starts with the two required dimensions only; the SelectCOA checkbox picker adds
            // the optional dimensions into selectedCOA, which MagicBox then renders an input for. coaTypes is
            // seeded from the constant so the picker renders without a network wait.
            return { ...dsInitialState, recordBeingEdited: record, coaTypes: SEGMENT_COA_TYPES };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []),
    );

    const record: any = state.recordBeingEdited;
    const ledger = record?.datasource ?? DS.GENERAL_LEDGER;
    const selectedCOA: Map<string, string> = record?.dataKeyInput?.selectedCOA ?? new Map<string, string>();

    /*
     * Load the per-dimension LOVs ourselves (mirrors CalculationBuilderView), so this sub-form doesn't
     * depend on calc-builder state. Each getCoaData is a hook keyed on its query, and the hook skips the
     * fetch entirely for an empty query — so passing '' for an unselected dimension is how we stay lazy
     * while keeping the hook call itself unconditional (hooks cannot be called conditionally).
     *
     * Only Company and GL Account load eagerly: they are the two required rows and always rendered. The six
     * optional dimensions fetch when their picker checkbox is ticked, which is the first moment their input
     * exists to be typed into. These lists can be very large (the deleted SegmentCoaAutosuggest carried the
     * same warning), so fetching all eight up front cost eight round-trips for dimensions most columns never
     * use.
     */
    const lovQueryFor = (ui: string, webApi: string) => (selectedCOA.has(ui) ? webApi : '');
    const company = services.dataSourcesService.getCoaData(M.COMPANY.WEB_API);
    const glAccount = services.dataSourcesService.getCoaData(M.GL_ACCOUNT.WEB_API);
    const location = services.dataSourcesService.getCoaData(lovQueryFor(M.LOCATION.UI, M.LOCATION.WEB_API));
    const costCenter = services.dataSourcesService.getCoaData(lovQueryFor(M.COST_CENTER.UI, M.COST_CENTER.WEB_API));
    const productLine = services.dataSourcesService.getCoaData(lovQueryFor(M.PRODUCT_LINE.UI, M.PRODUCT_LINE.WEB_API));
    const channel = services.dataSourcesService.getCoaData(lovQueryFor(M.CHANNEL.UI, M.CHANNEL.WEB_API));
    const project = services.dataSourcesService.getCoaData(lovQueryFor(M.PROJECT.UI, M.PROJECT.WEB_API));
    const lgCode = services.dataSourcesService.getCoaData(lovQueryFor(M.LG_CODE.UI, M.LG_CODE.WEB_API));

    /*
     * Each getCoaData returns [lov, loading, error]. The map entry for a dimension is written ONLY once its
     * fetch has resolved, because MagicBox distinguishes the two states by presence:
     * `statusType={lov == undefined ? 'loading' : 'finished'}`. getCoaData seeds its list to `[]` rather than
     * undefined, so eagerly writing `lov ?? []` would report every in-flight dimension as finished-and-empty
     * and the Autosuggest would say "No matches found" while the request was still running. Omitting the key
     * until it lands matches how the CLI builds the same map — CalculationBuilderReducer's
     * SET_SEVEN_SEGMENT_LOV `set`s one segment at a time into an initially empty Map, so an unloaded segment
     * reads back as undefined and renders the loading state.
     */
    const LOVS: [string, [string[], boolean, string]][] = [
        [M.COMPANY.UI, company],
        [M.GL_ACCOUNT.UI, glAccount],
        [M.LOCATION.UI, location],
        [M.COST_CENTER.UI, costCenter],
        [M.PRODUCT_LINE.UI, productLine],
        [M.CHANNEL.UI, channel],
        [M.PROJECT.UI, project],
        [M.LG_CODE.UI, lgCode],
    ];
    const sevenSegmentLOV = useMemo(
        () => {
            const map = new Map<string, string[]>();
            LOVS.forEach(([ui, [lov, loading]]) => {
                if (!loading) {
                    map.set(ui, lov ?? []);
                }
            });
            return map;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        LOVS.flatMap(([, [lov, loading]]) => [lov, loading]),
    );

    // A failed LOV fetch would otherwise be indistinguishable from a dimension that legitimately has no
    // values — the picker would just offer an empty list. Surface it instead, naming the dimensions that
    // failed, since the user's remaining option is to retry or type a known code.
    const lovErrors = LOVS.filter(([, [, , error]]) => !!error).map(([ui]) => ui);

    // Map the reused record into the nested criteria. Deliberately NOT memoised on `record`: the reused
    // DataSourcesReducer mutates `record.dataKeyInput` in place (see SET_DATA_KEY_INPUT /
    // SET_DATA_KEY_COA_INPUT) and returns the same `recordBeingEdited` reference, so an identity-keyed memo
    // would never recompute and an entered Company / GL Account would never reach the criteria — the counters
    // would show the value while validation still rejected it as missing. Mapping on every render is cheap
    // (a handful of comma-splits), and `onChange` is still gated on the mapped JSON below so a render that
    // doesn't change the criteria cannot loop.
    const criteria = toCriteria(record, state.customCoaReference, M, DS);
    const criteriaKey = JSON.stringify(criteria);
    useEffect(() => {
        onChange(criteria);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [criteriaKey]);

    /*
     * The options are DATA_SOURCE_TYPES values ("General Ledger" / "Secondary Ledger"), not LedgerType
     * members, because the field writes `record.datasource` — the reused leaves gate on that exact value
     * (SelectCOA's isLgCodeDisabledForRow, MagicBox's visibleCoaTypes, and the reducer's LG-Code clearing all
     * compare against DATA_SOURCE_TYPES.SECONDARY_LEDGER). `toCriteria` maps it back to
     * LedgerType.PRIMARY/SECONDARY on the way out. Using LedgerType directly here would mean translating for
     * three consumers instead of one, so the data-source type is the proxy the reuse asks for.
     */
    const LEDGER_OPTIONS = [
        { value: DS.GENERAL_LEDGER, label: DS.GENERAL_LEDGER },
        { value: DS.SECONDARY_LEDGER, label: DS.SECONDARY_LEDGER },
    ];

    return (
        <SpaceBetween size="m">
            <FormField label="Ledger type" description="Secondary Ledger unlocks the LG Code data key.">
                <Select
                    selectedOption={LEDGER_OPTIONS.find((o) => o.value === ledger) ?? LEDGER_OPTIONS[0]}
                    options={LEDGER_OPTIONS}
                    onChange={({ detail }) =>
                        dispatch(DS_ACTIONS.UPDATE_RECORD_FIELD.withPayload({ fieldName: 'datasource', value: detail.selectedOption.value }))
                    }
                />
            </FormField>

            {lovErrors.length > 0 && (
                <Alert type="warning" header="Some value lists could not be loaded">
                    {`Could not load the list of values for: ${lovErrors.join(', ')}. You can still type a known code, or close and reopen this form to retry.`}
                </Alert>
            )}

            <FormField stretch label="Data keys (COA)" description="Company and GL Account are required; check others to narrow the pull.">
                <div className="segmentGlPullDataSource">
                    <div className="segmentGlPullDataKeys">
                        <DataSourcesSelectCOA
                            showDrillDownSelect={false}
                            recordBeingEdited={{ ...record, entityId: record?.datasourceId }}
                            sevenSegmentLOV={sevenSegmentLOV}
                            allRecordDescriptions={[]}
                            originalRecordDescription=""
                            customCoaReference={state.customCoaReference}
                            coaTypes={state.coaTypes}
                            isSaving={false}
                            saveButtonLabel="Save data source"
                            /* Hides the row-level Save/Cancel bar — a segment column commits via the form's
                               own "Add column". Consequence worth knowing: SelectCOA runs its LOV-membership
                               check (CommonDataSourceOperations.validateCOAInput, for Company, GL Account and
                               every selectedCOA entry) inside saveRecord, which the hidden bar never invokes,
                               and no saveRecordRef is passed. So a hand-typed value outside the LOV is NOT
                               rejected inline here — validatePullCriteria checks presence, not membership.
                               Value validity is deliberately deferred to the pull; surfacing it inline would
                               mean driving saveRecord's validation without its save. */
                            hideActionButtons
                            dispatch={dispatch}
                            services={services}
                            onRecordNeedsSaving={() => { /* no-op: the column form commits via "Add column" */ }}
                        />
                        <DataSourcesMagicBox
                            recordBeingEdited={record}
                            services={services}
                            dispatch={dispatch}
                            showDecimals={false}
                            coaTypes={state.coaTypes}
                            sevenSegmentLOV={sevenSegmentLOV}
                            selectedCustomCoaValues={state.selectedCustomCoaValues}
                            allowBalancePull={false}
                            ACTIONS={DS_ACTIONS}
                        />
                    </div>
                </div>
            </FormField>
        </SpaceBetween>
    );
}

/**
 * A dimension's comma-joined value string → COADataKey[], dropping blanks.
 *
 * Exported for test: the blank/rollup filter is load-bearing and invisible downstream — a regression that
 * stops resolving a CUSTOM rollup's `tableId` silently drops the rollup rather than failing.
 */
export function toKeyList(commaString: string | undefined, ref: Map<string, string>): COADataKey[] {
    return CommonDataSourceOperations.convertToCOADataKeyList(commaString, ref)
        .filter((k) => (k.value && k.value.trim()) || k.tableId);
}

/**
 * Map the reused DataSourceRecord into the nested SegmentPullCriteria (ledgerType + dataKeys).
 *
 * Exported for test rather than left module-private: this is the producer half of the mapping, reachable
 * otherwise only by mounting the component and driving the reused reducer, and it is where the
 * "counter shows the value but the criteria is empty" bug lived.
 *
 * Company and GL Account come from their own `dataKeyInput` fields; every optional dimension comes from
 * `selectedCOA`, keyed by the `.UI` string, and lands on the `SegmentDataKeys` field named by that
 * dimension's `.WEB_API` — so the picker list and the emitted keys cannot drift apart.
 *
 * NOTE on the package boundary: `channels` is correct for this contract (it matches
 * `COA_SEGMENT_MAPPING.CHANNEL.WEB_API` and the Smithy `SegmentDataKeys`), but the pull-balance request
 * calls the same dimension `geoMarkets` (`CommonDataSourceOperations.convertToPullBalanceRequest` sets
 * `coaDataKeys.geoMarkets` from the Channel entry). The rename therefore has to happen on the consuming
 * side, in the GL-pull consumer that reads the stored criteria — not here. Getting that wrong yields a pull
 * that filters on nothing rather than an error.
 */
export function toCriteria(record: any, customCoaReference: Map<string, string> | undefined, M: any, DS: any):
    Pick<SegmentPullCriteria, 'ledgerType' | 'dataKeys'> {
    const ref = customCoaReference ?? new Map<string, string>();
    const dk = record?.dataKeyInput ?? {};
    const coa: Map<string, string> = dk.selectedCOA ?? new Map<string, string>();
    const dims: SegmentDataKeys = {
        [M.COMPANY.WEB_API]: toKeyList(dk.selectedCompanies, ref),
        [M.GL_ACCOUNT.WEB_API]: toKeyList(dk.selectedAccounts, ref),
    };
    OPTIONAL_COA_DIMENSIONS.forEach(({ ui, key }) => {
        dims[key] = toKeyList(coa.get(ui), ref);
    });
    // Emit only non-empty dimensions, so an unticked (or ticked-but-blank) dimension is absent rather than
    // present-and-empty — the pull scopes on what is there.
    const dataKeys: SegmentDataKeys = {};
    (Object.keys(dims) as (keyof SegmentDataKeys)[]).forEach((k) => {
        if (dims[k] && dims[k]!.length) {
            dataKeys[k] = dims[k];
        }
    });
    const ledgerType = record?.datasource === DS.SECONDARY_LEDGER ? LedgerType.SECONDARY : LedgerType.PRIMARY;
    return { ledgerType, dataKeys };
}
